import React, { useEffect, useRef } from 'react';
import RoundedPhoto from '../components/RoundedPhoto/RoundedPhoto';
import IntroFeature from '../components/IntroFeature/IntroFeature';
import PhotoSlides from '../components/PhotoSlides';

import '../styles/global.css';
import '../styles/HomePage.css';

import profile from '../data/profile.json';
import intro from '../data/intro.json';

function HomePage() {
  const pageWrapperRef = useRef(null);

  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      const wrapper = pageWrapperRef.current;

      if (hash && wrapper) {
        const target = document.getElementById(hash);
        if (target) {
          wrapper.scrollTo({ top: target.offsetTop, left: 0 });
        }
      } else {
        wrapper?.scrollTo({ top: 0, left: 0 });
      }
    };

    requestAnimationFrame(scrollToHash);
    window.addEventListener('hashchange', scrollToHash);

    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  return (
    <div className="page-wrapper home-page-wrapper" ref={pageWrapperRef}>
      <div className="page home-page">
        <section className="home-panel home-intro text-center">
          <h1 className="text-4xl font-bold">{profile.name} {profile.surname}</h1>
          <h2 className="pb-3 text-2xl font-thin">Mathematician, Software Engineer, LLM Tamer, Mechanic </h2>
          <div className="mx-auto lg:w-11/12">
            <p className="mb-0 mt-12 text-lg">
              Hello 😊
            </p>
            <p className="mb-0 text-lg">
              I'm Fırat, a Staff AI and Software Engineer based in beautiful Alsace, France.
            </p>
          <p className="mb-0 text-lg">
              My path started in mathematics: I earned a PhD with distinction in hyperbolic geometry, then moved into software engineering, cloud platforms, and applied AI.
            </p>
            <p className="mb-0 text-lg">
              That mix still defines how I work: rigorous when systems get complex, curious when problems are unclear, and practical when something needs to ship.
            </p>
            <p className="mb-0 text-lg">
              Today, I build LLM applications, agents, backend services, data pipelines, and cloud infrastructure.
            </p>
            <p className="mb-0 text-lg">
              Python is my everyday language; Haskell remains one of my favorites for its functional clarity.
            </p>
            <p className="mb-0 text-lg">
              I speak English 🇺🇸 and French 🇫🇷 fluently, and I'm currently learning German 🇩🇪.
            </p>
            <p className="mb-0 pb-5 text-lg">
              Outside work, I enjoy hiking in Alsace, playing guitar, and writing about AI, software, mathematics, and engineering craft.
            </p>
            <RoundedPhoto
              picture="profile/firat-kehl.JPEG"
              name={profile.name}
              surname={profile.surname}
              imageSize={220}
            />
          </div>
        </section>

        <section className="home-panel home-about mx-auto px-3 py-5" id="featured-3">
          <div className="home-about-heading">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
              About me
            </p>
            <h2 className="mt-2 text-3xl font-bold text-[var(--text-primary)] md:text-4xl">
              Software, mathematics, and craft
            </h2>
            <p className="mt-3 max-w-3xl text-lg leading-relaxed text-[var(--text-secondary)]">
              A few paths that shape how I think, build, and collaborate.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 py-5 text-center lg:grid-cols-3">
            {intro.map((intro, index) => (
              <IntroFeature
                key={index}
                title={intro.title}
                paragraph={intro.paragraph}
                image={intro.image}
                link={intro.link}
                linkText={intro.linkText}
              />
            ))}
          </div>
        </section>

        <section className="home-panel home-carousel" id="moments">
          <div className="home-carousel-heading">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
              Moments
            </p>
            <h2 className="mt-2 text-3xl font-bold text-[var(--text-primary)] md:text-4xl">
              Away from the keyboard
            </h2>
            <p className="mt-3 max-w-3xl text-lg leading-relaxed text-[var(--text-secondary)]">
              A small carousel of ordinary places, walks, and companions.
            </p>
          </div>
          <PhotoSlides />
        </section>
      </div>
    </div>
  );
}

export default HomePage;
